'use client'

import { memo } from 'react'
import {
  HIGHLIGHTED_STATS,
  type MagicStats,
  type TrackerCopy,
  type TrackerLocale,
  type TrackerPhase,
} from '@/lib/santa-tracker'
import { formatCompact, formatDuration } from './format'

/**
 * Tarjetas de cifras mágicas. Qué cuatro se muestran depende de la fase: en
 * preparativos interesan los regalos y las cartas; en pleno viaje, los
 * kilómetros y los países.
 */

const ICONS: Record<keyof MagicStats, string> = {
  giftsReady: '🎁',
  giftsDelivered: '🎁',
  lettersReceived: '✉️',
  elvesWorking: '🧝',
  reindeerTraining: '🦌',
  kilometers: '🧭',
  citiesVisited: '🏙️',
  countriesVisited: '🌍',
  homesWaiting: '🏠',
  travelMinutes: '⏱️',
}

function formatStat(
  key: keyof MagicStats,
  stats: MagicStats,
  copy: TrackerCopy,
  locale: TrackerLocale
): string {
  if (key === 'travelMinutes') return formatDuration(stats.travelMinutes, locale)
  if (key === 'kilometers') {
    return `${formatCompact(stats.kilometers, locale)} ${copy.stats.units.kilometers}`
  }
  return formatCompact(stats[key], locale)
}

export interface TrackerStatsProps {
  copy: TrackerCopy
  locale: TrackerLocale
  phase: TrackerPhase
  stats: MagicStats
}

export const TrackerStats = memo(function TrackerStats({
  copy,
  locale,
  phase,
  stats,
}: TrackerStatsProps) {
  return (
    <section aria-labelledby="tracker-stats-heading" className="space-y-4">
      <h2
        id="tracker-stats-heading"
        className="font-christmas text-2xl font-semibold md:text-3xl"
      >
        {copy.stats.heading[phase]}
      </h2>
      <dl className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
        {HIGHLIGHTED_STATS[phase].map((key) => (
          <div
            key={key}
            className="rounded-2xl border border-white/10 bg-white/5 p-4 text-center backdrop-blur-sm"
          >
            <span aria-hidden className="text-2xl">
              {ICONS[key]}
            </span>
            <dd className="mt-1 text-xl font-bold tabular-nums text-white md:text-2xl">
              {formatStat(key, stats, copy, locale)}
            </dd>
            <dt className="mt-1 text-xs text-white/70 md:text-sm">{copy.stats.labels[key]}</dt>
          </div>
        ))}
      </dl>
    </section>
  )
})
